import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { WalletTransactionType } from '@klaro7/shared-types';
import { Button, Card, CardContent, CardHeader, CardTitle, Spinner, Alert, AlertDescription, cn } from '@klaro7/ui';

import { extractErrorMessage } from '@/lib/api';
import { trackGoogleAdsPurchase } from '@/features/cookie-consent/analytics/googleAdsEvents';
import { trackMetaPixelEvent } from '@/features/cookie-consent/analytics/metaPixelEvents';
import { usePublicConfig } from '@/features/settings/hooks/useServicesSettings';
import {
  useWallet,
  useCreateRechargeCheckout,
  useConfirmRecharge,
  useWalletTransactions,
  useRedeemFreeMinutes,
  useRechargePackages,
} from '@/features/wallet/hooks/useWallet';
import { hasCompletedFirstRecharge, RECOMMENDED_RECHARGE_AMOUNT } from '@/features/wallet/lib/firstRechargePromo';
import { downloadTransactionReceipt } from '@/features/wallet/services/wallet.service';

const transactionTypeLabels: Record<string, string> = {
  [WalletTransactionType.RECHARGE]: 'Ricarica',
  [WalletTransactionType.CONSULTATION_CHARGE]: 'Consulenza',
  [WalletTransactionType.FREE_MINUTES_CREDIT]: 'Minuti Gratis',
  [WalletTransactionType.REFUND]: 'Rimborso',
  [WalletTransactionType.RECHARGE_BONUS]: 'Bonus Ricarica',
  [WalletTransactionType.CALL_CHARGE]: 'Chiamata',
  [WalletTransactionType.CHAT_CHARGE]: 'Chat',
  [WalletTransactionType.CREDIT_RESERVATION]: 'Riserva Credito',
  [WalletTransactionType.RESERVATION_RELEASE]: 'Rilascio Riserva',
  [WalletTransactionType.ADMIN_CREDIT]: 'Credito omaggio',
};

export function WalletPage() {
  const { data: wallet, isLoading: walletLoading } = useWallet();
  const checkoutMutation = useCreateRechargeCheckout();
  const confirmMutation = useConfirmRecharge();
  const redeemMutation = useRedeemFreeMinutes();
  const { data: packagesData } = useRechargePackages();
  const packages = packagesData?.packages ?? [];
  // Il bonus pacchetto (Art. 3.2/4.3 CG) spetta solo alla prima ricarica:
  // dalla seconda in poi i tagli restano ma senza bonus né credito extra.
  const bonusEligible = !hasCompletedFirstRecharge(wallet);

  const [txPage, setTxPage] = useState(1);
  const { data: transactionsData, isLoading: txLoading } = useWalletTransactions({ page: txPage, limit: 10 });

  const [rechargeOpen, setRechargeOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  // Amount currently being sent to Stripe Checkout (drives per-button loading state).
  const [pendingCheckoutAmount, setPendingCheckoutAmount] = useState<number | null>(null);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { data: publicConfig } = usePublicConfig();
  const metaEventsRef = useRef(publicConfig?.metaPixelEvents);
  metaEventsRef.current = publicConfig?.metaPixelEvents;
  const adsPurchaseSendToRef = useRef(publicConfig?.googleAdsPurchaseSendTo);
  adsPurchaseSendToRef.current = publicConfig?.googleAdsPurchaseSendTo;

  const successTimerRef = useRef<ReturnType<typeof setTimeout>>();
  // Guards the return-from-Stripe handler so it runs at most once per mount.
  const returnHandledRef = useRef(false);

  useEffect(() => {
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, []);

  const showSuccess = (msg: string) => {
    if (successTimerRef.current) clearTimeout(successTimerRef.current);
    setSuccessMessage(msg);
    successTimerRef.current = setTimeout(() => setSuccessMessage(''), 5000);
  };

  // Handle the redirect back from Stripe Checkout (?recharge=success|cancel).
  useEffect(() => {
    const recharge = searchParams.get('recharge');
    if (!recharge) return;
    if (returnHandledRef.current) return;
    returnHandledRef.current = true;

    if (recharge === 'success') {
      const sessionId = searchParams.get('session_id');
      // Strip the query params immediately so a refresh / back never re-triggers.
      navigate('/dashboard/wallet', { replace: true });

      if (!sessionId) {
        toast.error('Sessione di pagamento mancante.');
        return;
      }

      confirmMutation
        .mutateAsync(sessionId)
        .then((result) => {
          // Conversione Meta: Purchase SOLO alla prima ricarica dell'utente, se
          // l'evento è abilitato da admin (default ON). Non condizionato a
          // `credited`: se il webhook ha vinto la race la sessione è comunque
          // pagata (no-op senza consenso profilazione). Il re-fire è impedito
          // da returnHandledRef + strip immediato dei query params.
          if (
            result.isFirstRecharge &&
            typeof result.amount === 'number' &&
            metaEventsRef.current?.purchase !== false
          ) {
            trackMetaPixelEvent('Purchase', { value: result.amount, currency: 'EUR' });
          }
          // Conversione Google Ads: a differenza di Meta parte a OGNI ricarica,
          // così le campagne ottimizzano sul valore e non solo sull'acquisizione.
          // `transaction_id` = id sessione Stripe: è ciò che impedisce a Google
          // di contare due volte lo stesso pagamento.
          if (typeof result.amount === 'number') {
            trackGoogleAdsPurchase(adsPurchaseSendToRef.current, {
              value: result.amount,
              transactionId: sessionId,
            });
          }
          if (result.credited) {
            const balanceMsg =
              typeof result.balance === 'number'
                ? ` Nuovo saldo: € ${result.balance.toFixed(2)}.`
                : '';
            const msg = `Ricarica completata.${balanceMsg}`;
            toast.success(msg);
            showSuccess(msg);
          } else {
            toast.info('Pagamento in elaborazione. Il saldo verrà aggiornato a breve.');
          }
        })
        .catch((e) => {
          toast.error(extractErrorMessage(e));
        });
    } else if (recharge === 'cancel') {
      navigate('/dashboard/wallet', { replace: true });
      toast.info('Ricarica annullata');
    } else {
      navigate('/dashboard/wallet', { replace: true });
    }
    // We intentionally run this only when the query params change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleRecharge = async (amount: number) => {
    setErrorMessage('');
    setSuccessMessage('');
    setPendingCheckoutAmount(amount);
    try {
      const { url } = await checkoutMutation.mutateAsync(amount);
      window.location.assign(url);
    } catch (e) {
      setPendingCheckoutAmount(null);
      const msg = extractErrorMessage(e);
      setErrorMessage(msg);
      toast.error(msg);
    }
  };

  // Tracks which transaction's receipt is currently downloading (per-row spinner).
  const [downloadingReceiptId, setDownloadingReceiptId] = useState<string | null>(null);

  const handleDownloadReceipt = async (transactionId: string) => {
    setDownloadingReceiptId(transactionId);
    try {
      await downloadTransactionReceipt(transactionId);
    } catch (e) {
      toast.error(extractErrorMessage(e) || 'Impossibile scaricare la ricevuta');
    } finally {
      setDownloadingReceiptId(null);
    }
  };

  const handleRedeemFreeMinutes = async () => {
    setErrorMessage('');
    setSuccessMessage('');
    try {
      await redeemMutation.mutateAsync();
      showSuccess('7 Minuti Gratis riscattati con successo!');
    } catch (e) {
      setErrorMessage(e instanceof Error ? e.message : 'Errore durante il riscatto');
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 sm:px-6 lg:px-8">
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Borsellino</h1>
          <p className="text-neutral-500">Gestisci il tuo saldo e le ricariche</p>
        </div>

        {successMessage && (
          <Alert variant="success">
            <AlertDescription>{successMessage}</AlertDescription>
          </Alert>
        )}

        {errorMessage && (
          <Alert variant="error">
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}

        {/* Dual-wallet separation: real money (Ricarica) vs bonus credit (Bonus). */}
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="grid gap-4 sm:grid-cols-2">
            {/* Borsellino Ricarica — real money */}
            <Card className="border-brand-teal/30 bg-white shadow-sm">
              <CardContent className="flex flex-col items-center justify-center py-6 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-teal/10 text-brand-teal">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a2.25 2.25 0 00-2.25-2.25H15a3 3 0 11-6 0H5.25A2.25 2.25 0 003 12m18 0v6a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 18v-6m18 0V9M3 12V9m18 0a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 9m18 0V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v3" />
                  </svg>
                </div>
                <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-brand-teal">
                  Borsellino Ricarica
                </p>
                {walletLoading ? (
                  <Spinner size="md" className="mt-2" />
                ) : (
                  <p className="mt-1 text-3xl font-bold text-neutral-900">
                    {wallet ? `€ ${wallet.balance.toFixed(2)}` : '—'}
                  </p>
                )}
                <p className="mt-1 text-[11px] text-neutral-400">Credito acquistato</p>
              </CardContent>
            </Card>

            {/* Borsellino Bonus — promotional credit */}
            <Card className="border-brand-orange/30 bg-white shadow-sm">
              <CardContent className="flex flex-col items-center justify-center py-6 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
                  </svg>
                </div>
                <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-brand-orange">
                  Borsellino Bonus
                </p>
                {walletLoading ? (
                  <Spinner size="md" className="mt-2" />
                ) : (
                  <p className="mt-1 text-3xl font-bold text-neutral-900">
                    {wallet ? `€ ${(wallet.bonusBalance || 0).toFixed(2)}` : '—'}
                  </p>
                )}
                <p className="mt-1 text-[11px] text-neutral-400">Promozioni e regali</p>
              </CardContent>
            </Card>

            {/* Combined total — shown secondarily */}
            <div className="sm:col-span-2 flex items-center justify-center gap-2 rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-2.5">
              <span className="text-sm text-neutral-500">Totale disponibile</span>
              <span className="text-lg font-bold text-neutral-900">
                {wallet ? `€ ${((wallet.bonusBalance || 0) + wallet.balance).toFixed(2)}` : '—'}
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <Button
              size="lg"
              variant="primary"
              className="w-full bg-brand-orange hover:bg-brand-orange/90"
              onClick={() => setRechargeOpen(!rechargeOpen)}
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Ricarica Borsellino
            </Button>

            {wallet && !wallet.freeMinutesUsed && !hasCompletedFirstRecharge(wallet) && (
              <Button
                size="lg"
                variant="secondary"
                className="w-full"
                onClick={handleRedeemFreeMinutes}
                loading={redeemMutation.isPending}
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Riscatta 7 Minuti Gratis
              </Button>
            )}
          </div>
        </div>

        {/* Recharge Flow */}
        {rechargeOpen && (
          <Card className="border-neutral-200 bg-white shadow-sm">
            <CardHeader>
              <CardTitle className="text-neutral-900">Ricarica Borsellino</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-neutral-500">
                Seleziona un pacchetto di ricarica. Verrai reindirizzato al pagamento sicuro
                tramite Stripe.
              </p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" role="group" aria-label="Pacchetti ricarica">
                {packages.map((pkg) => {
                  const isPending = pendingCheckoutAmount === pkg.amount;
                  const anyPending = checkoutMutation.isPending || pendingCheckoutAmount !== null;
                  const isRecommended = pkg.amount === RECOMMENDED_RECHARGE_AMOUNT;
                  return (
                    <div
                      key={pkg.amount}
                      className={cn(
                        'relative flex flex-col rounded-lg border-2 px-4 py-4',
                        isRecommended
                          ? 'border-brand-orange bg-brand-orange/5 shadow-md ring-1 ring-brand-orange/20 sm:-translate-y-1'
                          : 'border-neutral-200 bg-neutral-50',
                      )}
                    >
                      {isRecommended && (
                        <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-brand-orange px-3 py-0.5 text-[11px] font-bold uppercase tracking-wide text-white shadow-sm">
                          Più conveniente
                        </span>
                      )}
                      <div className="flex items-center justify-between">
                        <span
                          className={cn(
                            'text-lg font-semibold',
                            isRecommended ? 'text-brand-navy' : 'text-neutral-700',
                          )}
                        >
                          Ricarica €{pkg.amount}
                        </span>
                        {bonusEligible && pkg.bonus > 0 && (
                          <span
                            className={cn(
                              'rounded-full px-2.5 py-0.5 text-xs font-semibold',
                              isRecommended
                                ? 'bg-brand-orange/15 text-brand-orange'
                                : 'bg-emerald-50 text-emerald-600',
                            )}
                          >
                            +€{pkg.bonus} bonus
                          </span>
                        )}
                      </div>
                      <p className="mt-1.5 text-sm text-neutral-500">
                        Ricevi €{bonusEligible ? pkg.totalCredit : pkg.amount} di credito
                      </p>
                      <Button
                        variant="primary"
                        className="mt-3 w-full bg-brand-orange hover:bg-brand-orange/90"
                        onClick={() => handleRecharge(pkg.amount)}
                        disabled={anyPending}
                        loading={isPending}
                      >
                        Ricarica €{pkg.amount}
                      </Button>
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-end border-t border-neutral-200 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setRechargeOpen(false)}
                  disabled={checkoutMutation.isPending || pendingCheckoutAmount !== null}
                >
                  Annulla
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Transaction History */}
        <Card className="border-neutral-200 bg-white shadow-sm">
          <CardHeader>
            <CardTitle className="text-neutral-900">Storico Transazioni</CardTitle>
          </CardHeader>
          <CardContent>
            {txLoading ? (
              <div className="flex justify-center py-8">
                <Spinner size="lg" />
              </div>
            ) : !transactionsData?.data?.length ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="mb-4 rounded-full bg-neutral-100 p-4">
                  <svg className="h-8 w-8 text-neutral-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="font-medium text-neutral-600">Nessuna transazione</p>
                <p className="mt-1 text-sm text-neutral-400">Le transazioni appariranno qui</p>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  {transactionsData.data.map((tx) => {
                    const isCredit =
                      tx.type === WalletTransactionType.RECHARGE ||
                      tx.type === WalletTransactionType.FREE_MINUTES_CREDIT ||
                      tx.type === WalletTransactionType.REFUND ||
                      tx.type === WalletTransactionType.RECHARGE_BONUS ||
                      tx.type === WalletTransactionType.RESERVATION_RELEASE ||
                      tx.type === WalletTransactionType.ADMIN_CREDIT;

                    return (
                      <div
                        key={tx.id}
                        className="flex items-center justify-between rounded-lg border border-neutral-200 p-4 transition-colors hover:bg-neutral-50"
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={`flex h-8 w-8 items-center justify-center rounded-full ${
                              isCredit ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-500'
                            }`}
                          >
                            {isCredit ? (
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m6-6H6" />
                              </svg>
                            ) : (
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M18 12H6" />
                              </svg>
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-neutral-900">
                              {transactionTypeLabels[tx.type] || tx.type}
                            </p>
                            <p className="text-xs text-neutral-500">
                              {new Date(tx.createdAt).toLocaleDateString('it-IT', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className={`text-sm font-semibold ${isCredit ? 'text-emerald-600' : 'text-red-500'}`}>
                              {isCredit ? '+' : '-'}€{Math.abs(tx.amount).toFixed(2)}
                            </p>
                            {/* balanceAfter = saldo TOTALE (bonus + reale) dopo la riga. */}
                            <p className="text-xs text-neutral-500">
                              Saldo totale: €{tx.balanceAfter.toFixed(2)}
                            </p>
                          </div>
                          {tx.type === WalletTransactionType.RECHARGE && (
                            <button
                              type="button"
                              onClick={() => handleDownloadReceipt(tx.id)}
                              disabled={downloadingReceiptId === tx.id}
                              className="flex items-center gap-1 rounded-lg border border-neutral-200 px-2.5 py-1.5 text-xs font-medium text-brand-teal transition-colors hover:bg-brand-teal/5 disabled:opacity-60"
                              title="Scarica ricevuta PDF"
                            >
                              {downloadingReceiptId === tx.id ? (
                                <Spinner size="sm" />
                              ) : (
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75} aria-hidden="true">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                                </svg>
                              )}
                              <span className="hidden sm:inline">Ricevuta</span>
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination */}
                {transactionsData.pagination && transactionsData.pagination.totalPages > 1 && (
                  <div className="mt-4 flex items-center justify-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!transactionsData.pagination.hasPrevPage}
                      onClick={() => setTxPage((p) => p - 1)}
                    >
                      Precedente
                    </Button>
                    <span className="text-sm text-neutral-500">
                      Pagina {transactionsData.pagination.page} di {transactionsData.pagination.totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!transactionsData.pagination.hasNextPage}
                      onClick={() => setTxPage((p) => p + 1)}
                    >
                      Successiva
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
